import { summarizeMechanicalVisitsInRange } from './maintenanceOverviewData';

const DAY_MS = 24 * 60 * 60 * 1000;

function calendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
}

const dateISO = date => date.toISOString().slice(0, 10);

// Match the in-suite calendar: Sunday starts the week, and month views use
// the full calendar month. UTC arithmetic operates on dates independently of DST.
export function getMechanicalCalendarRange(anchorISO, viewMode) {
  const anchor = calendarDate(anchorISO);
  if (!anchor || !['week', 'month'].includes(viewMode)) return { startDate: '', endDate: '' };
  if (viewMode === 'week') {
    const start = anchor.getTime() - anchor.getUTCDay() * DAY_MS;
    return { startDate: dateISO(new Date(start)), endDate: dateISO(new Date(start + 6 * DAY_MS)) };
  }
  const first = new Date(anchor);
  first.setUTCDate(1);
  const last = new Date(first);
  last.setUTCMonth(last.getUTCMonth() + 1, 0);
  return { startDate: dateISO(first), endDate: dateISO(last) };
}

export function shiftMechanicalCalendarDate(anchorISO, viewMode, direction) {
  const anchor = calendarDate(anchorISO);
  if (!anchor || !['week', 'month'].includes(viewMode) || !Number.isInteger(direction)) return '';
  if (viewMode === 'week') return dateISO(new Date(anchor.getTime() + direction * 7 * DAY_MS));

  // Setting the month on January 31 directly would skip February. Move from
  // the first, then clamp the original day to the destination month's length.
  const day = anchor.getUTCDate();
  anchor.setUTCDate(1);
  anchor.setUTCMonth(anchor.getUTCMonth() + direction);
  const last = new Date(anchor);
  last.setUTCMonth(last.getUTCMonth() + 1, 0);
  anchor.setUTCDate(Math.min(day, last.getUTCDate()));
  return dateISO(anchor);
}

export function getMechanicalCalendarVisits(rawVisits, range) {
  return summarizeMechanicalVisitsInRange(rawVisits, range);
}

// Same solid building colors as the in-suite calendar in MaintenanceSchedule.
const BUILDING_COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c',
  '#ca8a04', '#16a34a', '#0d9488', '#0891b2', '#4f46e5',
  '#9333ea', '#c026d3', '#059669', '#0284c7', '#7c2d12',
];

function buildingColorKey(visit) {
  if (visit.buildingObjectId) return `object:${visit.buildingObjectId}`;
  if (visit.buildingId) return `building:${visit.buildingId}`;
  if (visit.buildingName) return `name:${visit.buildingName}`;
  return `visit:${visit.key}`;
}

export function getMechanicalCalendarColors(visits) {
  const items = (Array.isArray(visits) ? visits : []).filter(visit => visit && typeof visit === 'object');
  const buildings = [...new Set(items.map(buildingColorKey))].sort();
  const colors = new Map(buildings.map((key, index) => [key, BUILDING_COLORS[index % BUILDING_COLORS.length]]));
  return Object.fromEntries(items.map(visit => [visit.key, colors.get(buildingColorKey(visit))]));
}

// One segment spans consecutive calendar cells. Only week rows (or the visible
// range boundary) split a visit; independent lanes keep overlapping bars visible.
export function getMechanicalCalendarWeeks(visits, range) {
  const rangeStart = calendarDate(range?.startDate)?.getTime();
  const rangeEnd = calendarDate(range?.endDate)?.getTime();
  if (rangeStart == null || rangeEnd == null || rangeStart > rangeEnd) return [];

  const clippedVisits = (Array.isArray(visits) ? visits : []).flatMap(visit => {
    const actualStart = calendarDate(visit?.startDate)?.getTime();
    const actualEnd = calendarDate(visit?.endDate)?.getTime();
    if (actualStart == null || actualEnd == null || actualStart > actualEnd) return [];
    const start = Math.max(actualStart, rangeStart);
    const end = Math.min(actualEnd, rangeEnd);
    return start <= end ? [{ visit, start, end, actualStart, actualEnd }] : [];
  });

  const weeks = [];
  const firstSunday = rangeStart - new Date(rangeStart).getUTCDay() * DAY_MS;
  for (let weekStart = firstSunday; weekStart <= rangeEnd; weekStart += 7 * DAY_MS) {
    const weekEnd = weekStart + 6 * DAY_MS;
    const days = Array.from({ length: 7 }, (_, index) => {
      const day = weekStart + index * DAY_MS;
      return day < rangeStart || day > rangeEnd ? null : dateISO(new Date(day));
    });
    const segments = clippedVisits.flatMap(item => {
      const start = Math.max(item.start, weekStart);
      const end = Math.min(item.end, weekEnd);
      if (start > end) return [];
      return [{
        visit: item.visit,
        columnStart: (start - weekStart) / DAY_MS + 1,
        columnSpan: (end - start) / DAY_MS + 1,
        lane: 0,
        continuesBefore: item.actualStart < start,
        continuesAfter: item.actualEnd > end,
      }];
    }).sort((a, b) => a.columnStart - b.columnStart
      || b.columnSpan - a.columnSpan
      || String(a.visit.key).localeCompare(String(b.visit.key)));

    const laneEnds = [];
    segments.forEach(segment => {
      const freeLane = laneEnds.findIndex(end => end < segment.columnStart);
      segment.lane = freeLane === -1 ? laneEnds.length : freeLane;
      laneEnds[segment.lane] = segment.columnStart + segment.columnSpan - 1;
    });
    weeks.push({ days, segments, laneCount: laneEnds.length });
  }
  return weeks;
}
